import { Button, Checkbox, Input, Spinner } from "@aidkitorg/component-library";
import { useCallback, useEffect, useState } from "react";
import { usePost } from "../API";
import { useAsyncJobStatus } from "../Hooks/AsyncJobStatusCheck";
import type { Directory } from '@aidkitorg/aidkit/lib/directory';
import { extractReturnAsJson } from "@aidkitorg/types/lib/endpoints";

export default function DocumentAssistance() {
  const [loading, setLoading] = useState(false);
  const scheduleAnalysis = usePost('/ai/document/schedule_analysis');
  const analysisResult = usePost('/program/admin/async_job_result');
  const [attachmentTargetFields, setAttachmentTargetFields] = useState<string[]>([]);
  const [limitToAnalyses, setLimitToAnalyses] = useState<string[] | undefined>(undefined);
  const [uids, setUids] = useState<string[] | undefined>(undefined);
  const [limitToBlankFields, setLimitToBlankFields] = useState<boolean>(false);
  const { taskId, phase, setTaskId } = useAsyncJobStatus();
  const [result, setResult] = useState<extractReturnAsJson<Directory['/ai/document/rerun_analyses']> | null>(null);

  const handleScheduleAnalysis = useCallback(async () => {
    setLoading(true);
    setResult(null);
    const response = await scheduleAnalysis({
      attachmentTargetFields: attachmentTargetFields,
      uids: uids,
      limitToAnalyses: limitToAnalyses,
      limitToBlankFields: limitToBlankFields,
    });
    if ('taskId' in response) {
      setTaskId(response.taskId);
    }
  }, [attachmentTargetFields, uids, limitToAnalyses, limitToBlankFields, scheduleAnalysis]);

  useEffect(() => {
    // If the job is finished, fetch the result
    if (result === null && (phase === 'FINISHED' || phase === 'FAILED')) {
      if (taskId) {
        analysisResult({ taskId: taskId })
          .then((response) => {
            if (response.status === 'FINISHED') {
              setResult(response.result);
            }
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    }
  }, [phase, result, taskId, analysisResult]);

  return (
    <div>
      <div className="flex flex-col gap-2 mt-2 mb-4">
        <Input
          label="Attachment Target Fields"
          value={attachmentTargetFields}
          onChange={(e) => setAttachmentTargetFields(e.target.value.split(',').map(t => t.trim()))}
          required
        />
        <Input
          label="Uids - separated by commas"
          value={uids}
          onChange={(e) => setUids(e.target.value.split(',').map(t => t.trim()))}
        />
        <Input
          label="Limit to specific analyses - separated by commas"
          value={limitToAnalyses}
          onChange={(e) => setLimitToAnalyses(e.target.value.split(',').map(t => t.trim()))}
        />
        <div className="my-2">
          <Checkbox
            label="Limit to blank fields"
            checked={limitToBlankFields}
            onChange={e => setLimitToBlankFields(!limitToBlankFields)}
          />
        </div>
      </div>
      <div className="flex items-center gap-2 h-9">
        <Button
          onClick={handleScheduleAnalysis}
          disabled={loading}
        >
          {loading ? 'Running...' : 'Rerun Analyses'}
        </Button>
        <div>
          {loading && <Spinner size="small" className="p-0 h-9" />}
        </div>
      </div>

      {/* Results Display */}
      {(!loading && result) && (
        <div className="mt-4">
          {('error' in result && result.error) && (
            <div>
              <h2>Error</h2>
              <p>{result.error}</p>
            </div>
          )}
          {('successes' in result && result.successes) && (
            <div>
              <h2>result</h2>
              <p>Successes: {result.successes}</p>
              <p>Failures: {result.failures.count}</p>
              {result.failures && (
                <div>
                  <h3>Failures</h3>
                  <ul>
                    {result.failures.errors.map(error => (
                      <li key={error}><pre>{error}</pre></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}